import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import useSurveySettings from 'src/hooks/useSurveySettings';
import HttpService from 'src/services/api/http';
import { WidgetExportFilterType } from 'src/components/dashboard/DashboardPage/components/WidgetFrame/useWidgetExport';

import useFollowUpFilters from './useFollowUpFilters';
import useFollowUpFiltersVerbose from './useFollowUpFiltersVerbose';
import type { GlobalSurveyIsLoading } from './useGenerateWidgetEndpoint';
import { FETCHING_GLOBAL_SURVEY } from './useGenerateWidgetEndpoint';

async function fetchWidgetData<T>(
  endpoint: string,
  filters: Record<
    string,
    string | boolean | undefined | null | Record<string, string>
  >,
  signal?: AbortSignal,
) {
  // Date bounds and timeComparison are sent as flat query parameters with the other filters.
  return HttpService.get(endpoint, filters, undefined, {
    signal,
  })
    .then((data: AxiosResponse<T>) => Promise.resolve(data.data))
    .catch((error) => {
      // Network failures and timeouts have no HTTP response to inspect.
      if (
        !error.response ||
        error.response.status >= 500 ||
        error.response.status === 424 ||
        error.response.status === 404
      ) {
        return Promise.reject('somethingWentWrong');
      }
      if (Object.hasOwn(error.response.data, 'detail')) {
        return Promise.reject(String(error.response.data.detail));
      } else if (Object.hasOwn(error.response.data, 'error')) {
        return Promise.reject(String(error.response.data.error));
      }
      return Promise.reject(String(error.response.data));
    });
}

export type WidgetQueryData<WidgetResponse> = Exclude<
  UseQueryResult<WidgetResponse>,
  'error'
> & {
  errorMsg: string;
  queryFilters: Record<WidgetExportFilterType, string | undefined>;
  comparisonData?: WidgetResponse;
};

interface WidgetQueryOptions<T> {
  extraFilters?: Record<string, string>;
  noFilter?: boolean;
  // mockQueryFn is used during development to mock the response of the query as long as the backend is not ready yet
  mockQueryFn?: () => Promise<T>;
}

function useWidgetQuery<T>(
  endpoint: string | GlobalSurveyIsLoading,
  options?: WidgetQueryOptions<T>,
  queriesEnabled = true,
): WidgetQueryData<T> {
  const { i18n, t } = useTranslation();
  const { yearOverYearComparisonAvailable } = useSurveySettings();
  const {
    dateRange,
    followUpFilters,
    demographicsFilters,
    orgUnit,
    comparisonDateRange,
  } = useFollowUpFilters();

  const verboseFollowUpFilters = useFollowUpFiltersVerbose();

  const getRequestFilters = useCallback(
    (requestType: 'current' | 'comparison') => ({
      ...(orgUnit ? { orgUnit } : {}),
      ...(options?.noFilter
        ? {}
        : {
            ...(requestType === 'comparison' ? comparisonDateRange : dateRange),
            ...followUpFilters,
            ...demographicsFilters,
            ...(options?.extraFilters ?? {}),
            ...(requestType === 'comparison' && yearOverYearComparisonAvailable
              ? { timeComparison: true }
              : {}),
          }),
    }),
    [
      orgUnit,
      followUpFilters,
      demographicsFilters,
      dateRange,
      comparisonDateRange,
      options,
      yearOverYearComparisonAvailable,
    ],
  );
  const normalFiltersToUse = getRequestFilters('current');

  const isGlobalSurvey = endpoint === FETCHING_GLOBAL_SURVEY;
  const queryResults = useQuery<T, string>({
    enabled: !isGlobalSurvey && queriesEnabled,
    queryKey: [
      endpoint,
      i18n.language,
      !!options?.mockQueryFn,
      normalFiltersToUse,
    ],
    queryFn: ({ signal }) => {
      if (options?.mockQueryFn) {
        return options?.mockQueryFn();
      }
      return fetchWidgetData<T>(endpoint, normalFiltersToUse, signal);
    },
    keepPreviousData: true,
  });

  const comparisonEnabled =
    comparisonDateRange !== undefined && // User has checked comparison checkbox
    !options?.noFilter &&
    !isGlobalSurvey &&
    queriesEnabled;
  const comparisonFiltersToUse = getRequestFilters('comparison');
  // Keep hook order stable: always call useQuery even if comparisonEnabled is false.
  const comparisonQueryResults = useQuery<T, string>({
    queryKey: [
      endpoint,
      i18n.language,
      !!options?.mockQueryFn,
      comparisonFiltersToUse,
    ],
    queryFn: ({ signal }) => {
      if (options?.mockQueryFn) {
        return options?.mockQueryFn();
      }
      return fetchWidgetData<T>(endpoint, comparisonFiltersToUse, signal);
    },
    enabled: comparisonEnabled,
    keepPreviousData: comparisonEnabled,
  });

  // Like comparisonData below: the comparison query's loading state and error are only the widget's while
  // comparison is ON. With noFilter the comparison observer even watches the current query itself.
  const comparisonError = comparisonEnabled
    ? comparisonQueryResults.error
    : null;
  const comparisonFetching =
    comparisonEnabled && comparisonQueryResults.isFetching;

  const errorMsg = useMemo(() => {
    // A failed current request is never masked by a failed comparison request.
    const error = queryResults.error || comparisonError;
    if (error === 'somethingWentWrong') {
      return t('common:somethingWentWrong');
    }
    return error || '';
  }, [queryResults.error, comparisonError, t]);

  // Retry targets the failed request(s): a healthy current result is kept instead of being re-requested
  // because only the comparison failed.
  const refetch = useCallback(() => {
    if (!comparisonError) {
      return queryResults.refetch();
    }
    const comparisonRefetch = comparisonQueryResults.refetch();
    return queryResults.isError ? queryResults.refetch() : comparisonRefetch;
  }, [
    comparisonError,
    comparisonQueryResults.refetch,
    queryResults.isError,
    queryResults.refetch,
  ]);

  // Readable query filters for the xlsx export
  const queryFiltersForExport: Record<
    WidgetExportFilterType,
    string | undefined
  > = useMemo(
    () => ({
      [WidgetExportFilterType.RANGE]: options?.noFilter
        ? ''
        : verboseFollowUpFilters.dateRange,
      [WidgetExportFilterType.TIME_RANGE_COMPARISON]: options?.noFilter
        ? ''
        : verboseFollowUpFilters.comparisonDateRange,
      [WidgetExportFilterType.ORG_TREE]: options?.noFilter
        ? ''
        : verboseFollowUpFilters.orgUnits,
      [WidgetExportFilterType.DEMOGRAPHICS]: options?.noFilter
        ? ''
        : [
            verboseFollowUpFilters.demographicsFilters,
            verboseFollowUpFilters.followUpFilters,
          ]
            .filter((value) => value)
            .join('; '),
      [WidgetExportFilterType.SEARCH]: options?.extraFilters?.textFilter,
    }),
    [verboseFollowUpFilters, options],
  );

  return {
    ...queryResults,
    isFetching:
      endpoint === FETCHING_GLOBAL_SURVEY ||
      queryResults.isFetching ||
      comparisonFetching,
    refetch,
    errorMsg,
    queryFilters: queryFiltersForExport,
    comparisonData: comparisonEnabled ? comparisonQueryResults.data : undefined,
  };
}

export default useWidgetQuery;
